"use client";
import "react-responsive-modal/styles.css";
import { Modal } from "react-responsive-modal";
import { useState } from "react";
import { useDarkMode } from "@/hooks/useDarkMode";
import AnalyticsService from "@/services/analytics";
import { useI18N } from "common/hooks";
import ModalWithAction from "@/components/common/ModalWithAction";
import RenameList from "./RenameList";
import ImageCropProvider from "@/providers/ImageCropProvider";
import CoverPhotoCrop from "./CoverPhotoCrop";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shadcn/components/ui/dropdown-menu";

interface SavedPlace {
  cover_photo: string;
  created_at: string;
  id: number;
  name: string;
  saved_place_details: object;
  total_places: number;
  updated_at: string;
  user_id: number;
  // Add any other fields that are part of your saved place object
}

interface SavePlaceMenuProps {
  onDeleteClick: () => void;
  list: SavedPlace;
}

const SavePlaceMenu = ({ onDeleteClick, list }: SavePlaceMenuProps) => {
  const i18n = useI18N();
  const isDark = useDarkMode();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openRenameListModal, setOpenRenameListModal] = useState(false);
  const [openUpdateCoverPhotoModal, setOpenUpdateCoverPhotoModal] =
    useState(false);

  const menuItems = [
    {
      label: `${i18n.get("renameSavedList")}`,
      icon: "/images/PencilSimple_Primary500.svg",
      iconDark: "/images/PencilSimple_dark.svg",
      path: "#",
      onclick: () => {
        AnalyticsService.trackGAEventHandler("listSettingsClickedRenameList");
        AnalyticsService.trackMx("listSettingsClickedRenameList", {
          SavedPlaceName: list?.name,
          SavedPlaceId: list?.id,
          SavedPlaceTotalPlaces: list?.total_places,
        });
        setOpenRenameListModal(true);
      },
    },
    {
      label: `${i18n.get("updateCoverPhoto")}`,
      icon: "/images/Camera_Primary_500.svg",
      iconDark: "/images/Camera_dark.svg",
      path: "#",
      onclick: () => {
        AnalyticsService.trackGAEventHandler(
          "listSettingsClickedUpdateCoverPhoto",
        );
        AnalyticsService.trackMx("listSettingsClickedUpdateCoverPhoto", {
          SavedPlaceName: list?.name,
          SavedPlaceId: list?.id,
          SavedPlaceTotalPlaces: list?.total_places,
        });
        console.log("openUpdateCoverPhotoModal");
        setOpenUpdateCoverPhotoModal(true);
      },
    },
    {
      label: `${i18n.get("delete")}`,
      icon: "/images/Trash_primary_500.svg",
      iconDark: "/images/Trash_dark.svg",
      path: "#",
      onclick: () => {
        AnalyticsService.trackGAEventHandler("listSettingsClickedDeleteList");
        AnalyticsService.trackMx("listSettingsClickedDeleteList", {
          SavedPlaceName: list?.name,
          SavedPlaceId: list?.id,
          SavedPlaceTotalPlaces: list?.total_places,
        });

        setIsModalOpen(true);
      },
    },
  ];

  const handleDeleteClick = () => {
    onDeleteClick();
    setIsModalOpen(false);
  };

  const handleRenameListModalClose = () => {
    console.log("Closing Modal handleRenameListModalClose");
    setOpenRenameListModal(false);
  };

  const handleUpdateCoverPhotoModalClose = () => {
    console.log("Closing Modal handleUpdateCoverPhotoModalClose");
    setOpenUpdateCoverPhotoModal(false);
  };

  return (
    <div className="">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="inline-flex items-center"
            aria-label="List options"
          >
            {isDark ? (
              <img src="/images/DotsThree_dark.svg" alt="" aria-hidden="true" />
            ) : (
              <img
                src="/images/DotsThree_Priamry_800.svg"
                alt=""
                aria-hidden="true"
              />
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {menuItems.map((item, index) => (
            <div key={index}>
              <DropdownMenuItem onClick={item?.onclick} className="p-0">
                <div className="self-stretch min-w-28 min-h-11 px-3 py-2 inline-flex justify-center items-center gap-3 flex-wrap content-center">
                  <div className="flex-1 flex justify-start items-center gap-3 flex-wrap content-center">
                    <div className="h-6 flex justify-center items-center">
                      <div
                        data-format="Outline"
                        data-weight="Regular"
                        className="w-6 h-6 min-w-6 min-h-6 relative"
                      >
                        {isDark ? (
                          <img
                            src={item.iconDark}
                            alt=""
                            className="w-5 h-5 absolute left-[3px] top-[2.25px]"
                            aria-hidden="true"
                          />
                        ) : (
                          <img
                            src={item.icon}
                            alt=""
                            className="w-5 h-5 absolute left-[3px] top-[2.25px]"
                          />
                        )}
                      </div>
                    </div>
                    <div className="flex-1 min-h-6 inline-flex flex-col justify-center items-start">
                      <div className="self-stretch justify-center">
                        {item.label}
                      </div>
                    </div>
                  </div>
                </div>
              </DropdownMenuItem>
              {index < menuItems.length - 1 && <DropdownMenuSeparator />}
            </div>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <ModalWithAction
        type="error"
        actionButtonType="error"
        isModalOpen={isModalOpen}
        title={`Delete"${list?.name}"?`}
        subtitle={i18n.get("deleteListMessage")}
        cancelButtonTitle={i18n.get("cancel")}
        actionButtonTitle={i18n.get("delete")}
        onClose={() => {
          AnalyticsService.trackGAEventHandler(
            "deleteListClickedCancelDeleteList",
          );
          AnalyticsService.trackMx("deleteListClickedCancelDeleteList", {
            SavedPlaceName: list?.name,
            SavedPlaceId: list?.id,
            SavedPlaceTotalPlaces: list?.total_places,
          });

          setIsModalOpen(false);
        }}
        onClick={handleDeleteClick}
      />

      {openRenameListModal && (
        <Modal
          focusTrapped
          center
          blockScroll={true}
          animationDuration={0}
          open={openRenameListModal}
          onClose={() => handleRenameListModalClose()}
          classNames={{
            modal:
              "bg-[transparent] p-0 focus:outline-0 shadow-none md:w-[480px]",
            closeButton: "hidden",
          }}
        >
          <RenameList
            list={list}
            onClose={() => handleRenameListModalClose()}
          />
        </Modal>
      )}

      {openUpdateCoverPhotoModal && (
        <ImageCropProvider>
          <CoverPhotoCrop
            list={list}
            initiateOpenModal={true}
            onClose={() => handleUpdateCoverPhotoModalClose()}
          />
        </ImageCropProvider>
      )}
    </div>
  );
};

export default SavePlaceMenu;
